import { useCallback, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../../lib/useApi'
import { useRealtimeRefetch } from '../../lib/realtime'
import { haptics } from '../../lib/haptics'

const COLORS = ['#00f5ff', '#a855f7', '#ff6b35', '#3b82f6', '#22c55e', '#ef4444', '']

export default function KanbanPanel() {
  const api = useApi()
  const qc = useQueryClient()
  const [undo, setUndo] = useState(null)

  const { data: lists = [], isLoading: listsLoading } = useQuery({ queryKey: ['kb-lists'], queryFn: () => api.get('/kanban/lists') })
  const { data: cards = [] } = useQuery({ queryKey: ['kb-cards'], queryFn: () => api.get('/kanban/cards') })

  const refetch = useCallback(() => {
    qc.invalidateQueries({ queryKey: ['kb-lists'] })
    qc.invalidateQueries({ queryKey: ['kb-cards'] })
  }, [qc])
  useRealtimeRefetch('kanban_lists', refetch)
  useRealtimeRefetch('kanban_cards', refetch)

  const newList = useMutation({
    mutationFn: (title) => api.post('/kanban/lists', { title }),
    onSuccess: refetch,
  })

  const newCard = useMutation({
    mutationFn: ({ list_id, title }) => api.post('/kanban/cards', { list_id, title }),
    onSuccess: refetch,
  })

  const patchCard = useMutation({
    mutationFn: ({ id, body }) => api.patch(`/kanban/cards/${id}`, body),
    onSuccess: refetch,
  })

  const patchList = useMutation({
    mutationFn: ({ id, body }) => api.patch(`/kanban/lists/${id}`, body),
    onSuccess: refetch,
  })

  const bulkArchive = useMutation({
    mutationFn: (list_id) => api.post(`/kanban/cards/bulk-archive?list_id=${list_id}`),
    onSuccess: ({ archived_ids }) => {
      refetch()
      if (archived_ids?.length) {
        setUndo({ ids: archived_ids })
        setTimeout(() => setUndo(null), 6000)
      }
    },
  })

  function unarchiveAll(ids) {
    Promise.all(ids.map((id) => api.post(`/kanban/cards/${id}/unarchive`))).then(refetch)
    setUndo(null)
  }

  // ---- DnD ----
  const [dragId, setDragId] = useState(null)
  const [dragListId, setDragListId] = useState(null)

  const reorderLists = useMutation({
    mutationFn: (items) => api.post('/kanban/lists/reorder', items),
    onSuccess: refetch,
  })

  function onDrop(listId) {
    if (!dragId) return
    patchCard.mutate({ id: dragId, body: { list_id: listId } })
    setDragId(null)
    haptics.bump()
  }

  function onListDrop(targetListId) {
    if (!dragListId || dragListId === targetListId) { setDragListId(null); return }
    const sorted = [...lists].sort((a, b) => a.position - b.position)
    const fromIdx = sorted.findIndex((l) => l.id === dragListId)
    const toIdx   = sorted.findIndex((l) => l.id === targetListId)
    if (fromIdx < 0 || toIdx < 0) { setDragListId(null); return }
    const next = [...sorted]
    const [moved] = next.splice(fromIdx, 1)
    next.splice(toIdx, 0, moved)
    reorderLists.mutate(next.map((l, i) => ({ id: l.id, position: i })))
    haptics.bump()
    setDragListId(null)
  }

  // Add list inline
  const [newTitle, setNewTitle] = useState('')

  return (
    <section className="space-y-3">
      <div className="section-h">Kanban</div>

      <div className="flex gap-2 mb-3">
        <input value={newTitle} onChange={(e) => setNewTitle(e.target.value)}
               onKeyDown={(e) => e.key === 'Enter' && newTitle.trim() && (newList.mutate(newTitle.trim()), setNewTitle(''))}
               placeholder="+ Neue Liste"
               className="flex-1 bg-transparent border border-white/15 rounded-md px-3 py-2 text-sm" />
      </div>

      {listsLoading && lists.length === 0 && (
        <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2 animate-pulse">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="min-w-[260px] flex-1 max-w-xs border border-card rounded-themed p-3 h-40 bg-card" />
          ))}
        </div>
      )}
      {!listsLoading && lists.length === 0 && (
        <div className="text-sm opacity-60 text-center py-6 border border-dashed border-card rounded-themed">
          Noch keine Liste. Erste oben anlegen ↑
        </div>
      )}

      <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2">
        {lists.map((l) => {
          const listCards = cards.filter((c) => c.list_id === l.id)
          const doneCount = listCards.filter((c) => c.done).length
          return (
            <div key={l.id}
                 draggable
                 onDragStart={(e) => { e.stopPropagation(); setDragListId(l.id); haptics.tap() }}
                 className={`min-w-[260px] flex-1 max-w-xs border border-card rounded-themed p-3 ${dragListId === l.id ? 'opacity-50' : ''}`}
                 onDragOver={(e) => e.preventDefault()}
                 onDrop={(e) => { if (dragListId) onListDrop(l.id); else onDrop(l.id) }}
                 style={{ borderTopColor: l.color || undefined, borderTopWidth: l.color ? 2 : 1 }}>
              <header className="flex items-center justify-between mb-2 gap-2">
                <span className="opacity-30 hover:opacity-70 text-xs cursor-grab" title="Liste ziehen">⋮⋮</span>
                <input defaultValue={l.title}
                       onBlur={(e) => e.target.value !== l.title && patchList.mutate({ id: l.id, body: { title: e.target.value } })}
                       className="bg-transparent font-display text-sm tracking-widest uppercase outline-none w-full flex-1" />
                <div className="flex gap-1">
                  {COLORS.map((c) => (
                    <button key={c || 'none'}
                            onClick={() => patchList.mutate({ id: l.id, body: { color: c } })}
                            title={c || 'no color'}
                            className="w-3 h-3 rounded-full border border-white/20"
                            style={{ background: c || 'transparent' }} />
                  ))}
                </div>
              </header>

              <ul className="space-y-1.5 min-h-[40px]">
                {listCards.map((c) => (
                  <li key={c.id} draggable
                      onDragStart={() => setDragId(c.id)}
                      className={`px-2 py-1.5 rounded border text-sm cursor-grab active:cursor-grabbing ${
                        c.done ? 'border-white/5 opacity-50 line-through' : 'border-white/10 hover:border-[var(--accent)]/40'
                      }`}>
                    <div className="flex justify-between gap-2">
                      <span className="flex-1">{c.title}</span>
                      <button onClick={() => patchCard.mutate({ id: c.id, body: { done: !c.done } })}
                              className="opacity-50 hover:opacity-100">{c.done ? '↩' : '✓'}</button>
                    </div>
                  </li>
                ))}
              </ul>

              <AddCard onAdd={(title) => newCard.mutate({ list_id: l.id, title })} />

              {doneCount > 0 && (
                <button onClick={() => bulkArchive.mutate(l.id)}
                        className="mt-2 text-[10px] tracking-widest opacity-60 hover:text-[var(--accent)]">
                  ARCHIVE {doneCount} DONE
                </button>
              )}
            </div>
          )
        })}
      </div>

      {undo && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 border border-white/10 rounded-md px-4 py-2 flex items-center gap-3 z-30 animate-system-open"
             style={{ background: 'var(--bg)' }}>
          <span className="text-sm">{undo.ids.length} archiviert</span>
          <button onClick={() => unarchiveAll(undo.ids)} className="text-[var(--accent)] text-sm">Undo</button>
        </div>
      )}
    </section>
  )
}

function AddCard({ onAdd }) {
  const [t, setT] = useState('')
  return (
    <div className="mt-2">
      <input value={t} onChange={(e) => setT(e.target.value)}
             onKeyDown={(e) => e.key === 'Enter' && t.trim() && (onAdd(t.trim()), setT(''))}
             placeholder="+ Karte"
             className="w-full bg-transparent border border-dashed border-white/10 rounded px-2 py-1.5 text-xs" />
    </div>
  )
}
